import React from 'react';
import { cn } from '@/shared/ui/utils/tailwind';
import {
  SEMANTIC_VARIANTS,
  TONE_MAP,
  LEGACY_VARIANT_CLASSES,
  LEGACY_SIZE_CLASSES,
} from './Text.variants';

export type TextVariant = keyof typeof SEMANTIC_VARIANTS;
export type TextTone = keyof typeof TONE_MAP;

type TextProps = {
  /** Semantic variant backed by design tokens (preferred over raw size). */
  variant?: string;
  /** Tailwind t-shirt size (legacy Mantine compat; prefer variant). */
  size?: string;
  /** Font weight override (raw number or CSS value). */
  fw?: string | number;
  /** Color tone mapped to --text-* tokens. Overridden by explicit `c`. */
  tone?: TextTone;
  mt?: string | number;
  mb?: string | number;
  /** Raw color value (legacy). `"dimmed"` resolves to --text-secondary. */
  c?: string;
  ta?: string;
  tt?: string;
  fs?: string;
  as?: React.ElementType;
  component?: React.ElementType;
  span?: boolean;
  className?: string;
  style?: React.CSSProperties;
  children?: React.ReactNode;
  [key: string]: any;
};

/**
 * Unified Text component with semantic token-backed variants.
 *
 * Preferred usage (semantic):
 * ```tsx
 * <Text variant="bodySm" fw={500}>Title</Text>
 * <Text variant="meta" tone="tertiary">12 Aug 2026</Text>
 * ```
 *
 * Legacy (Mantine-compat, still supported):
 * ```tsx
 * <Text size="sm" fw={600}>Label</Text>
 * <Text variant="caption">Helper text</Text>
 * ```
 */
const Text: React.FC<TextProps> = ({
  variant = 'empty',
  size,
  fw,
  tone,
  mt,
  mb,
  c,
  ta,
  tt,
  fs: fontStyle,
  as,
  component,
  span,
  className,
  style,
  children,
  ...props
}) => {
  const Tag = component || as || (span ? 'span' : 'p');

  const semantic = SEMANTIC_VARIANTS[variant];
  const legacyVClass = !semantic ? (LEGACY_VARIANT_CLASSES[variant] || '') : '';
  const sClass = size ? LEGACY_SIZE_CLASSES[size] || '' : '';

  const resolvedColor = c
    ? (c === 'dimmed' ? 'var(--text-secondary)' : c)
    : (tone ? TONE_MAP[tone] : undefined);

  const computedStyle: React.CSSProperties = {
    ...(semantic ?? {}),
    ...(fw != null ? { fontWeight: fw as any } : {}),
    ...(resolvedColor ? { color: resolvedColor } : {}),
    ...(mt != null ? { marginTop: typeof mt === 'number' ? `${mt}px` : mt } : {}),
    ...(mb != null ? { marginBottom: typeof mb === 'number' ? `${mb}px` : mb } : {}),
    ...(ta ? { textAlign: ta as any } : {}),
    ...(tt ? { textTransform: tt as any } : {}),
    ...(fontStyle ? { fontStyle } : {}),
    ...style,
  };

  return (
    <Tag
      className={cn('whitespace-pre-wrap m-0', !resolvedColor && 'text-[var(--text-primary)]', legacyVClass, sClass, className)}
      style={Object.keys(computedStyle).length > 0 ? computedStyle : undefined}
      {...props}
    >
      {children}
    </Tag>
  );
};

export default Text;
